import React, { useCallback } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useStyles } from './styles';
import { IToolBoxItem } from './interfaces';

/**
 * One tool in the Admin Tools popover: a round icon with a caption.
 *
 * @param props.Icon - Icon shown in the circle.
 * @param props.title - Caption under the icon.
 * @param props.subtitle - Optional second line, muted; rendered only when given.
 * @param props.hint - Optional tooltip; wraps the item only when given.
 * @param props.handleClick - Click handler; not called while `disabled`.
 * @param props.disabled - Greys the item out and swallows clicks.
 * @param props.dataTestId - Test id of the item; the subtitle derives `${dataTestId}-subtitle`.
 */
const ToolBoxItem = ({
  Icon,
  title,
  subtitle,
  hint,
  handleClick,
  disabled = false,
  dataTestId,
}: IToolBoxItem) => {
  const [background, setBackground] = React.useState('transparent');

  const classes = useStyles(background, disabled);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (disabled) {
        return;
      }

      setBackground('green');

      setTimeout(() => {
        setBackground('transparent');
      }, 1000);
      handleClick(e);
    },
    [setBackground, handleClick, disabled],
  );

  const item = (
    <Box
      component="div"
      sx={classes.root}
      onClick={onClick}
      data-testid={dataTestId}
      aria-disabled={disabled || undefined}
    >
      <Box component="div" sx={classes.iconWrapper}>
        {Icon}
      </Box>
      <Typography sx={{ color: 'white' }}>{title}</Typography>
      {subtitle ? (
        // A span, not a second paragraph: the root's `& p` rule styles the
        // title and would push this line down by the same margin.
        <Typography
          component="span"
          sx={classes.subtitle}
          data-testid={dataTestId ? `${dataTestId}-subtitle` : undefined}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );

  if (!hint) {
    return item;
  }

  // A div, not a button, so the tooltip still opens on a disabled item — which
  // is exactly when the rule it explains matters.
  return (
    <Tooltip title={hint} arrow placement="bottom">
      {item}
    </Tooltip>
  );
};

export default ToolBoxItem;
